import { gql, useMutation, useQuery, useApolloClient } from "@apollo/client";
import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { isTechnician, isAdministrator, isCustomer } from "../../utils/roles";
import { useAuth } from "../../contexts/AuthContext";
import {
  LoadingView,
  AutoExpandingTextarea,
  NotificationContext,
  InvalidReasons,
  Tabs,
} from "@solsys/solsys-reactjs";
import { useUserList } from "../../contexts/UserListContext";
import TypeIndicatorDropDown from "../../dropdowns/TypeIndicatorDropDown";
import LocationIndicatorDropDown from "../../dropdowns/LocationIndicatorDropDown";
import CraneDropDown from "../../dropdowns/CraneDropDown";
import TicketStatusDropDown from "../../dropdowns/TicketStatusDropDown";
import TicketTypeDropDown from "../../dropdowns/TicketTypeDropDown";
import TicketEvent from "./TicketEvent";
import CraneEdit from "../crane/CraneEdit";
import { CRANE_EVENT_TYPE, TICKET_EVENT_TYPE } from "../../eventTypes";
import SaveButton from "../elements/SaveButton";
import {
  useCreateIdempotencyKeys,
  fingerprintProperties,
  mutationInput,
} from "../../utils/idempotency";

const entityQuery = gql`
  query ticketList($ticket_list_request: ticket_list_request) {
    ticket(ticket_list_request: $ticket_list_request) {
      edges {
        id
        name
        description
        ticket_type_id
        ticket_type {
          id
          name
        }
        status_id
        ticket_status {
          id
          name
        }
        ticket2type_indicator {
          edges {
            id
            type_indicator_id
            type_indicator {
              id
              name
            }
          }
        }
        ticket2location_indicator {
          edges {
            id
            location_indicator_id
            location_indicator {
              id
              name
            }
          }
        }
        crane {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const ticketSubscriptionsQuery = gql`
  query ticketSubscriptions($ticket_subscription_list_request: ticket_subscription_list_request) {
    ticket_subscription(ticket_subscription_list_request: $ticket_subscription_list_request) {
      edges {
        id
        user_id
        ticket_id
        created_at
      }
    }
  }
`;

const entityMutation = gql`
  mutation ticketMutate($ticket_mutation_request: MutationInput) {
    ticket(ticket_mutation_request: $ticket_mutation_request) {
      id
      isNew
    }
  }
`;

const entityTicketMutation = gql`
  mutation ticketEventMutate($ticket_event_mutation_request: MutationInput) {
    ticket_event(ticket_event_mutation_request: $ticket_event_mutation_request) {
      id
      isNew
    }
  }
`;

const entityCraneMutation = gql`
  mutation craneEventMutate($crane_event_mutation_request: MutationInput) {
    crane_event(crane_event_mutation_request: $crane_event_mutation_request) {
      id
      isNew
    }
  }
`;

const ticketSubscriptionMutation = gql`
  mutation ticketSubscriptionMutate($ticket_subscription_mutation_request: MutationInput) {
    ticket_subscription(ticket_subscription_mutation_request: $ticket_subscription_mutation_request) {
      id
      isNew
    }
  }
`;

const TICKET_TYPE_LABELS = {
  1: "Emergency",
  2: "Repair",
  3: "Maintenance",
};
const DEFAULT_TICKET_TYPE_ID = 2;

const ticket2TypeIndicatorMutation = gql`
  mutation ticket2TypeIndicatorMutate($ticket2type_indicator_mutation_request: MutationInput) {
    ticket2type_indicator(ticket2type_indicator_mutation_request: $ticket2type_indicator_mutation_request) {
      id
      isNew
    }
  }
`;

const ticket2LocationIndicatorMutation = gql`
  mutation ticket2LocationIndicatorMutate($ticket2location_indicator_mutation_request: MutationInput) {
    ticket2location_indicator(ticket2location_indicator_mutation_request: $ticket2location_indicator_mutation_request) {
      id
      isNew
    }
  }
`;

async function syncTicketJunctions({
  client,
  ticketId,
  selectedIds,
  existingEdges,
  indicatorKey,
  mutation,
  mutationRequestKey,
  createKeyFor,
}) {
  const selected = new Set(
    (Array.isArray(selectedIds) ? selectedIds : [selectedIds])
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id) && id > 0)
  );
  const existing = existingEdges ?? [];
  const existingByIndicator = new Map(
    existing.map((edge) => [Number(edge[indicatorKey]), edge])
  );

  for (const edge of existing) {
    const indicatorId = Number(edge[indicatorKey]);
    if (!selected.has(indicatorId)) {
      await client.mutate({
        mutation,
        variables: {
          [mutationRequestKey]: {
            id: edge.id,
            shouldDelete: true,
          },
        },
      });
    }
  }

  for (const indicatorId of selected) {
    if (!existingByIndicator.has(indicatorId)) {
      await client.mutate({
        mutation,
        variables: {
          [mutationRequestKey]: mutationInput({
            properties: [
              { key: "ticket_id", number: Number(ticketId) },
              { key: indicatorKey, number: indicatorId },
            ],
            idempotencyKey: createKeyFor ? createKeyFor(indicatorId) : undefined,
          }),
        },
      });
    }
  }
}

const serverUrl = process.env.REACT_APP_API_URL || "RUNTIME_REPLACE_REACT_APP_API_URL";

export default function TicketEdit({ id: entityId, action, navigate, options = {}, readOnly = false }) {
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);
  const [activeTab, setActiveTab] = useState("Chat");
  // New tickets open straight in the form; existing ones open as a read-only summary.
  const [isEditing, setIsEditing] = useState(!isEdit);

  let variables = {
    ticket_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId ?? 0),
        },
      ],
      sorting: [{
        direction: "ASC",
        field: "id"
      }]
    }
  };
  const userList = useUserList();
  const { user } = useAuth();
  const [ticketId, setTicketId] = useState(entityId);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ticketType, setTicketType] = useState(DEFAULT_TICKET_TYPE_ID);
  const [previousStatus, setPreviousStatus] = useState(null);
  const [status, setStatus] = useState(1);  // Default to Open
  const [typeIndicator, setTypeIndicator] = useState([]);
  const [locationIndicator, setLocationIndicator] = useState([]);
  const [typeIndicatorLinks, setTypeIndicatorLinks] = useState([]);
  const [locationIndicatorLinks, setLocationIndicatorLinks] = useState([]);
  const [crane, setCrane] = useState(options.crane_id || "");
  const [craneName, setCraneName] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [addSubscriberUserId, setAddSubscriberUserId] = useState(null);
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState(false);
  const [ticketEventKey, setTicketEventKey] = useState(0);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeComment, setCloseComment] = useState("");

  const [invalidReasons, setInvalidReasons] = useState([]);
  const createKeys = useCreateIdempotencyKeys();
  const ticketProperties = [
    { key: "name", string: name },
    { key: "description", string: description },
    { key: "ticket_type_id", number: Number(ticketType) },
    { key: "status_id", number: Number(status) },
    { key: "crane_id", number: crane ? Number(crane) : null },
  ];
  const ticketCreateFingerprint = fingerprintProperties([
    ...ticketProperties,
    { key: "type_indicator", string: JSON.stringify(typeIndicator) },
    { key: "location_indicator", string: JSON.stringify(locationIndicator) },
  ]);

  const client = useApolloClient();

  const isTechnicianUser = isTechnician(user);
  const isCustomerUser = isCustomer(user);
  const canManageSubscriptions = isAdministrator(user);

  const technicianUsers = useMemo(
    () => (userList ?? []).filter((u) => u.role === "technician"),
    [userList]
  );

  const availableTechnicians = useMemo(
    () => technicianUsers.filter(
      (tech) => !subscribers.some(
        (sub) => String(sub.user_id) === String(tech._id)
      )
    ),
    [technicianUsers, subscribers]
  );

  const loadSubscribers = useCallback(async (ticketId) => {
    if (!ticketId) {
      setSubscribers([]);
      return;
    }
    try {
      const response = await client.query({
        query: ticketSubscriptionsQuery,
        variables: {
          ticket_subscription_list_request: {
            filters: [
              { field: "ticket_id", operator: "=", value: String(ticketId) },
            ],
            pagination: { limit: 1000 },
          },
        },
        fetchPolicy: "network-only",
      });
      const edges = response?.data?.ticket_subscription?.edges ?? [];
      const mapped = edges.map((edge) => {
        const listUser = userList.find(
          (u) => String(u._id) === String(edge.user_id)
        );
        return {
          subscriptionId: edge.id,
          user_id: String(edge.user_id ?? ""),
          name: listUser?.name ?? listUser?.username ?? `User #${edge.user_id}`,
          username: listUser?.username ?? "",
          email: listUser?.email ?? "",
        };
      });
      setSubscribers(mapped.filter((entry) => entry.user_id));
    } catch (error) {
      console.error("Failed to load ticket subscribers:", error);
    }
  }, [client, userList]);

  const {
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const listResponse = localData?.ticket;
      const list = listResponse?.edges ?? [];
      const ticket = list[0];

      setName(ticket?.name ?? "");
      setDescription(ticket?.description ?? "");
      setTicketType(ticket?.ticket_type?.id ?? ticket?.ticket_type_id ?? DEFAULT_TICKET_TYPE_ID);
      const loadedStatus = ticket?.status_id ?? 1;
      setStatus(loadedStatus);
      setPreviousStatus(loadedStatus);
      const typeEdges = ticket?.ticket2type_indicator?.edges ?? [];
      const locationEdges = ticket?.ticket2location_indicator?.edges ?? [];
      setTypeIndicatorLinks(typeEdges);
      setLocationIndicatorLinks(locationEdges);
      setTypeIndicator(typeEdges.map((e) => e.type_indicator_id));
      setLocationIndicator(locationEdges.map((e) => e.location_indicator_id));
      if (options.crane_id) {
        setCrane(options.crane_id);
      } else if (ticket?.crane?.id) {
        setCrane(ticket.crane.id);
      }
      setCraneName(ticket?.crane?.name ?? null);

      if (ticket?.id) {
        loadSubscribers(ticket.id);
      }
    },
    onError: (error) => {
      addNotification("bad", "Failed to load ticket data");
    },
  });

  const validateForm = () => {
    const reasons = [];
    if (!name || name.trim() === "") reasons.push("Name is required.");
    if (!description.trim()) reasons.push("Description is required.");
    if (!ticketType) reasons.push("Type is required.");
    if (status === null) reasons.push("Status is required.");
    if (!typeIndicator?.length) reasons.push("Type Indicator is required.");
    if (!locationIndicator?.length) reasons.push("Location Indicator is required.");
    if (crane === null || crane === "") reasons.push("Crane is required.");

    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Check if user is trying to close ticket (status changing from open to closed)
    if (previousStatus === 1 && status === 2) {
      setShowCloseDialog(true);
      return;
    }

    if (isEdit) mutateFunction();
    else createKeys.trackSave(() => mutateFunction());
  };

  const handleCancel = () => {
    setInvalidReasons([]);
    // Discard unsaved edits by reloading the persisted values.
    refetchEntity();
    setIsEditing(false);
  };

  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      ticket_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: ticketProperties,
        idempotencyKey: isEdit
          ? undefined
          : createKeys.keyFor("ticket", ticketCreateFingerprint),
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Failed to save ticket");
    },
    onCompleted: async (response) => {
      let id = response?.ticket?.id;
      const isNewTicket = response?.ticket?.isNew;
      setTicketId(id);

      try {
        await syncTicketJunctions({
          client,
          ticketId: id,
          selectedIds: typeIndicator,
          existingEdges: typeIndicatorLinks,
          indicatorKey: "type_indicator_id",
          mutation: ticket2TypeIndicatorMutation,
          mutationRequestKey: "ticket2type_indicator_mutation_request",
          createKeyFor: (indicatorId) =>
            createKeys.keyFor(`type:${indicatorId}`, `${id}:${indicatorId}`),
        });
        await syncTicketJunctions({
          client,
          ticketId: id,
          selectedIds: locationIndicator,
          existingEdges: locationIndicatorLinks,
          indicatorKey: "location_indicator_id",
          mutation: ticket2LocationIndicatorMutation,
          mutationRequestKey: "ticket2location_indicator_mutation_request",
          createKeyFor: (indicatorId) =>
            createKeys.keyFor(`loc:${indicatorId}`, `${id}:${indicatorId}`),
        });
      } catch (error) {
        console.error("Failed to sync ticket indicators:", error);
        addNotification("bad", "Ticket saved but indicator links may be incomplete");
      }

      if (isNewTicket && isTechnicianUser) {
        try {
          await client.mutate({
            mutation: ticketSubscriptionMutation,
            variables: {
              ticket_subscription_mutation_request: mutationInput({
                properties: [
                  { key: "ticket_id", number: Number(id) },
                  { key: "user_id", string: user._id },
                ],
                idempotencyKey: createKeys.keyFor("autosub", `${id}:${user._id}`),
              }),
            },
          });
          await loadSubscribers(id);
        } catch (error) {
          console.error("Failed to auto-subscribe to ticket:", error);
        }
      }

      const ticketTypeLabel = TICKET_TYPE_LABELS[ticketType] || "Ticket";
      const isClosed = Number(status) === 2;
      const statusMessage = isClosed ? "Closed" : "Opened";
      const ticketEventTypeId = isClosed ? TICKET_EVENT_TYPE.CLOSED : TICKET_EVENT_TYPE.OPEN;
      const craneEventTypeId = isClosed ? CRANE_EVENT_TYPE.TICKET_CLOSED : CRANE_EVENT_TYPE.TICKET_OPENED;

      if (isNewTicket) {
        const trimmedCloseComment = (closeComment || "").trim();
        const ticketEventMessage = isClosed && trimmedCloseComment
          ? `Ticket #${id} (${ticketTypeLabel}) ${statusMessage} by ${user.name ?? user.username} for Crane #${crane}. Comment: ${trimmedCloseComment}`
          : `Ticket #${id} (${ticketTypeLabel}) ${statusMessage} by ${user.name ?? user.username} for Crane #${crane}`;
        await sendTicketEventMessage({
          message: ticketEventMessage,
          ticketId: id,
          client,
          user,
          typeId: ticketEventTypeId,
        });
        if (crane) {
          await sendCraneEventMessage({
            message: isClosed
              ? `Ticket #${id} "${name}" (${ticketTypeLabel}) ${statusMessage} by ${user.name ?? user.username}`
              : `New ${ticketTypeLabel} ticket #${id} "${name}" opened by ${user.name ?? user.username}`,
            craneId: crane,
            client,
            user,
            typeId: craneEventTypeId,
          });
        }
        setTicketEventKey(prev => prev + 1);
      } else if (status !== previousStatus) {
        const trimmedCloseComment = (closeComment || "").trim();
        const eventMessage = isClosed && trimmedCloseComment
          ? `Ticket #${id} (${ticketTypeLabel}) ${statusMessage} by ${user.name ?? user.username} for Crane #${crane}. Comment: ${trimmedCloseComment}`
          : `Ticket #${id} (${ticketTypeLabel}) ${statusMessage} by ${user.name ?? user.username} for Crane #${crane}`;

        await sendTicketEventMessage({
          message: eventMessage,
          ticketId: id,
          client,
          user,
          typeId: ticketEventTypeId,
        });
        await sendCraneEventMessage({
          message: `Ticket #${id} "${name}" (${ticketTypeLabel}) ${statusMessage} by ${user.name ?? user.username}`,
          craneId: crane,
          client,
          user,
          typeId: craneEventTypeId,
        });
        setTicketEventKey(prev => prev + 1);
      }

      navigate({
        action: "edit",
        id,
        push: false,
      });

      setPreviousStatus(status);
      refetchEntity();
      setIsEditing(false);
      const actionMessage = isNewTicket
        ? `Ticket #${id} "${name}" created successfully`
        : `Ticket #${id} "${name}" saved successfully`;
      addNotification("good", actionMessage);
    },
  });

  const handleAddSubscriber = async () => {
    if (!canManageSubscriptions || !ticketId || !addSubscriberUserId) return;
    const selectedUser = technicianUsers.find(
      (u) => String(u._id) === String(addSubscriberUserId)
    );
    if (!selectedUser) return;

    const alreadySubscribed = subscribers.some(
      (sub) => String(sub.user_id) === String(addSubscriberUserId)
    );
    if (alreadySubscribed) {
      addNotification("bad", "That technician is already assigned.");
      return;
    }

    setSubscriptionActionLoading(true);
    try {
      await client.mutate({
        mutation: ticketSubscriptionMutation,
        variables: {
          ticket_subscription_mutation_request: mutationInput({
            properties: [
              { key: "ticket_id", number: Number(ticketId) },
              { key: "user_id", string: String(addSubscriberUserId) },
            ],
            idempotencyKey: createKeys.keyFor(
              `sub:${addSubscriberUserId}`,
              `${ticketId}:${addSubscriberUserId}`
            ),
          }),
        },
      });

      const subscriberName = selectedUser.name ?? selectedUser.username;
      await sendTicketEventMessage({
        message: `${user.name ?? user.username} assigned ${subscriberName} to ticket #${ticketId}`,
        ticketId,
        client,
        user,
        typeId: TICKET_EVENT_TYPE.ASSIGNED,
      });

      setAddSubscriberUserId(null);
      await loadSubscribers(ticketId);
      setTicketEventKey((prev) => prev + 1);
      addNotification("good", `${subscriberName} assigned to this ticket.`);
    } catch (error) {
      console.error("Failed to subscribe technician:", error);
      addNotification("bad", "Failed to assign technician to ticket.");
    } finally {
      setSubscriptionActionLoading(false);
    }
  };

  const handleRemoveSubscriber = async (subscription) => {
    if (!canManageSubscriptions || !ticketId || !subscription?.subscriptionId) return;

    setSubscriptionActionLoading(true);
    try {
      await client.mutate({
        mutation: ticketSubscriptionMutation,
        variables: {
          ticket_subscription_mutation_request: {
            id: subscription.subscriptionId,
            shouldDelete: true,
          },
        },
      });

      await sendTicketEventMessage({
        message: `${user.name ?? user.username} unassigned ${subscription.name} from ticket #${ticketId}`,
        ticketId,
        client,
        user,
        typeId: TICKET_EVENT_TYPE.UNASSIGN,
      });

      await loadSubscribers(ticketId);
      setTicketEventKey((prev) => prev + 1);
      addNotification("good", `${subscription.name} unassigned from this ticket.`);
    } catch (error) {
      console.error("Failed to unsubscribe technician:", error);
      addNotification("bad", "Failed to unassign technician from ticket.");
    } finally {
      setSubscriptionActionLoading(false);
    }
  };

  const sendTicketEventMessage = async ({ message, ticketId, client, user, typeId = TICKET_EVENT_TYPE.MESSAGE }) => {
    if (!message || !ticketId || !user) return;

    try {
      await client.mutate({
        mutation: entityTicketMutation,
        variables: {
          ticket_event_mutation_request: mutationInput({
            properties: [
              { key: "type_id", number: Number(typeId) },
              { key: "message", string: message },
              { key: "ticket_id", number: Number(ticketId) },
              { key: "user_id", string: user._id },
              {
                key: "user_display_name",
                string: user.name ?? user.username,
              },
              {
                key: "url",
                string: window.location.href,
              },
            ],
            idempotencyKey: createKeys.keyFor(
              `ticketEvent:${typeId}`,
              `${ticketId}:${typeId}:${message}`
            ),
          }),
        },
      });
    } catch (error) {
      console.error("Failed to send ticket event message:", error);
    }
  };

  const sendCraneEventMessage = async ({ message, craneId, client, user, typeId = CRANE_EVENT_TYPE.MESSAGE }) => {
    if (!message || !craneId || !user) return;

    try {
      await client.mutate({
        mutation: entityCraneMutation,
        variables: {
          crane_event_mutation_request: mutationInput({
            properties: [
              { key: "type_id", number: Number(typeId) },
              { key: "message", string: message },
              { key: "crane_id", number: Number(craneId) },
              { key: "user_id", string: user._id },
              {
                key: "user_display_name",
                string: user.name ?? user.username,
              },
              {
                key: "url",
                string: window.location.href,
              },
            ],
            idempotencyKey: createKeys.keyFor(
              `craneEvent:${typeId}`,
              `${craneId}:${typeId}:${message}`
            ),
          }),
        },
      });
    } catch (error) {
      console.error("Failed to send crane event message:", error);
    }
  };

  const handleOpenCloseDialog = () => {
    setStatus(2);
    setShowCloseDialog(true);
  };

  const handleCloseTicket = async () => {
    try {
      await mutateFunction();
      setCloseComment("");
      setShowCloseDialog(false);
    } catch (err) {
      console.error("Error closing ticket:", err);
      addNotification("bad", "Failed to close ticket");
    }
  };

  const handleCancelClose = () => {
    setCloseComment("");
    setShowCloseDialog(false);
    // Reset status back to previous value
    setStatus(previousStatus);
  };

  useEffect(() => {
    refetchEntity();
  }, [entityId]);

  useEffect(() => {
    if (isEdit && entityId) {
      loadSubscribers(entityId);
    }
  }, [entityId, isEdit, loadSubscribers]);

  // Read-only callers never edit; everyone else toggles via the Edit button.
  const showForm = readOnly ? false : isEditing;
  const statusLabel = status === 2 ? "Closed" : status === 1 ? "Open" : "—";
  const ticketTypeLabel = TICKET_TYPE_LABELS[ticketType] ?? "—";
  const typeIndicatorNames = (typeIndicatorLinks ?? [])
    .map((e) => e?.type_indicator?.name)
    .filter(Boolean);
  const locationIndicatorNames = (locationIndicatorLinks ?? [])
    .map((e) => e?.location_indicator?.name)
    .filter(Boolean);

  return loadingEntity ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          { readOnly ? "Ticket Detail" : (isEdit ? "Edit" : "Add") + " Ticket" }
        </span>
        { options.crane_id && (
          <div className="page-header-options">
            <button
              type="button"
              onClick={ () => navigate({ action: "list", id: null }) }
            >
              Back to list
            </button>
          </div>
        ) }
      </div>

      <div className="add-edit">
        <div className="entity-edit-form">
          <div className="entity-form-header">
            <span className="entity-form-title">
              { name || (isEdit ? `Ticket #${entityId}` : "New Ticket") }
            </span>
            { !readOnly && !isEditing && (
              <div className="entity-form-header-actions">
                { status === 1 && isEdit && (
                  <button
                    className="edit-toggle close-toggle"
                    onClick={ handleOpenCloseDialog }
                    type="button"
                  >
                    Close
                  </button>
                ) }
                <button
                  className="edit-toggle"
                  onClick={ () => setIsEditing(true) }
                  type="button"
                >
                  Edit
                </button>
              </div>
            ) }
          </div>

          { showForm ? (
            <>
        <div className="edit-section edit-grid">
          <div className="edit-property">
            <label>Ticket Type *</label>
            <TicketTypeDropDown
              selected={ ticketType }
              onSelect={ setTicketType }
              searchEnabled={ false }
              multiple={ false }
              disabled={ readOnly }
            />
          </div>

          <div className="edit-property">
            <label>Name *</label>
            <input
              value={ name }
              onChange={ (e) => setName(e.target.value) }
              placeholder="Enter ticket name"
              disabled={ readOnly }
            />
          </div>

          { !isCustomerUser && (
            <div className="edit-property">
              <label>Status *</label>
              <TicketStatusDropDown
                selected={ status }
                onSelect={ setStatus }
                disabled={ readOnly }
              />
            </div>
          ) }

          <div className="edit-property">
            <label>Crane *</label>
            <CraneDropDown
              selected={ crane }
              onSelect={ setCrane }
              searchEnabled
              multiple={ false }
              disabled={ readOnly || !!options.crane_id }
            />
          </div>

          <div className="edit-property">
            <label>Type Indicator *</label>
            <TypeIndicatorDropDown
              selected={ typeIndicator }
              onSelect={ setTypeIndicator }
              searchEnabled
              multiple
              disabled={ readOnly }
            />
          </div>

          <div className="edit-property">
            <label>Location Indicator *</label>
            <LocationIndicatorDropDown
              selected={ locationIndicator }
              onSelect={ setLocationIndicator }
              searchEnabled
              multiple
              disabled={ readOnly }
            />
          </div>

          <div className="edit-property full-width">
            <label>Description *</label>
            <AutoExpandingTextarea
              value={ description }
              onChange={ (e) => setDescription(e.target.value) }
              placeholder="Enter ticket description"
              minRows={ 3 }
              disabled={ readOnly }
            />
          </div>

          <div className="edit-property full-width">
            <label>Assigned Technicians</label>
            { subscribers.length === 0 ? (
              <p>No assigned technicians yet</p>
            ) : (
              <ul>
                { subscribers.map((subscriber) => (
                  <li key={ subscriber.subscriptionId } style={ { marginBottom: "0.35rem" } }>
                    <strong>{ subscriber.name }</strong>
                    { subscriber.username ? ` (${subscriber.username})` : "" }
                    { subscriber.email ? ` — ${subscriber.email}` : "" }
                    { canManageSubscriptions && !readOnly && (
                      <button
                        type="button"
                        onClick={ () => handleRemoveSubscriber(subscriber) }
                        disabled={ subscriptionActionLoading }
                        style={ { marginLeft: "0.75rem" } }
                      >
                        Remove
                      </button>
                    ) }
                  </li>
                )) }
              </ul>
            ) }
            { canManageSubscriptions && !readOnly && isEdit && (
              <div style={ { display: "flex", gap: "0.5rem", alignItems: "flex-end", marginTop: "0.75rem" } }>
                <div style={ { flex: 1 } }>
                  <label style={ { display: "block", marginBottom: "0.25rem" } }>
                    Assign a technician:
                  </label>
                  <select
                    value={ addSubscriberUserId ?? "" }
                    onChange={ (e) => setAddSubscriberUserId(e.target.value || null) }
                    disabled={ subscriptionActionLoading || availableTechnicians.length === 0 }
                    style={ { width: "100%", padding: "6px 10px" } }
                  >
                    <option value="">
                      { availableTechnicians.length === 0
                        ? "No available technicians"
                        : "Select technician..." }
                    </option>
                    { availableTechnicians.map((tech) => (
                      <option key={ tech._id } value={ tech._id }>
                        { tech.name ?? tech.username }
                      </option>
                    )) }
                  </select>
                </div>
                <SaveButton
                  onClick={ handleAddSubscriber }
                  saving={ subscriptionActionLoading }
                  disabled={ !addSubscriberUserId }
                >
                  Assign
                </SaveButton>
              </div>
            ) }
          </div>
        </div>
        { invalidReasons.length > 0 && (
          <InvalidReasons reasons={ invalidReasons } />
        ) }
        <div className="edit-actions">
          { isEdit && (
            <button className="secondary" onClick={ handleCancel }>
              Cancel
            </button>
          ) }
          <SaveButton
            saving={ !isEdit && createKeys.isSaving }
            disabled={ isEdit && mutateInfo.loading }
            onClick={ () => handleSave() }
          />
        </div>
            </>
          ) : (
            <div className="view-grid">
              <div className="view-item">
                <label>Status</label>
                <span className={ `status-badge status-badge--${status === 2 ? "closed" : "open"}` }>
                  { statusLabel }
                </span>
              </div>
              <div className="view-item">
                <label>Ticket Type</label>
                <span className="view-value">{ ticketTypeLabel }</span>
              </div>
              <div className="view-item">
                <label>Crane</label>
                <span className={ craneName ? "view-value" : "view-value muted" }>
                  { craneName || (crane ? `Crane #${crane}` : "Not set") }
                </span>
              </div>
              <div className="view-item">
                <label>Type Indicator</label>
                <span className={ typeIndicatorNames.length ? "view-value" : "view-value muted" }>
                  { typeIndicatorNames.length ? typeIndicatorNames.join(", ") : "None" }
                </span>
              </div>
              <div className="view-item">
                <label>Location Indicator</label>
                <span className={ locationIndicatorNames.length ? "view-value" : "view-value muted" }>
                  { locationIndicatorNames.length ? locationIndicatorNames.join(", ") : "None" }
                </span>
              </div>
              <div className="view-item full-width">
                <label>Description</label>
                <span className={ description ? "view-value" : "view-value muted" }>
                  { description || "No description" }
                </span>
              </div>
              <div className="view-item full-width">
                <label>Assigned Technicians</label>
                <span className={ subscribers.length ? "view-value" : "view-value muted" }>
                  { subscribers.length
                    ? subscribers.map((s) => s.name).join(", ")
                    : "None assigned" }
                </span>
              </div>
            </div>
          ) }
        </div>
      </div>

      { ticketId && (
        <div className="ticket-edit-tabs">
          <Tabs autoSelect state={ [activeTab, setActiveTab] }>
            <div label="Chat">
              <TicketEvent
                key={ ticketEventKey }
                ticket_id={ Number(ticketId) }
                crane_id={ crane ? Number(crane) : undefined }
              />
            </div>
            <div label="Crane">
              { crane ? (
                <CraneEdit
                  id={ Number(crane) }
                  action="edit"
                  navigate={ navigate }
                  readOnly={ readOnly }
                  tab="Chat"
                />
              ) : (
                <div style={ { padding: "20px", textAlign: "center" } }>
                  No crane associated with this ticket.
                </div>
              ) }
            </div>
          </Tabs>
        </div>
      ) }

      { showCloseDialog && (
        <div className="dialog-backdrop">
          <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="close-ticket-header">
            <h3 id="close-ticket-header">Close Ticket</h3>
            <p>Optionally add a final comment before closing this ticket:</p>
            <textarea
              value={ closeComment }
              onChange={ (e) => setCloseComment(e.target.value) }
              rows={ 4 }
              placeholder="Optional closing comment..."
              style={ { width: "100%" } }
            />
            <div className="dialog-actions">
              <button type="button" onClick={ handleCancelClose }>Cancel</button>
              <button type="button" onClick={ handleCloseTicket }>
                Confirm Close
              </button>
            </div>
          </div>
        </div>
      ) }
    </>
  );
}
